import { getFormattedUnit } from "config";
import React, { useEffect, useRef, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { useSnackbar } from "notistack";
import { findQuotationLineItem } from "./quotationLineHelpers";
import StaticPopupNew from "components/StaticPopupNew";

const parseNumber = (value, fallback = 0) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getDiscountedItemRate = (basePrice, discount) => {
  const numericBasePrice = parseNumber(basePrice);
  const numericDiscount = parseNumber(discount);
  return numericBasePrice - (numericBasePrice * numericDiscount) / 100;
};

const getMaterialQuantity = (material) => parseNumber(material?.quantity);

const getPlainMaterialName = (value, fallback = "material") => {
  const plainText = String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

  return plainText || fallback;
};

const normalizeRateInput = (value) => {
  if (!/^\d*\.?\d{0,2}$/.test(value)) return null;
  if (value.length > 1 && /^0+[1-9]/.test(value)) {
    return value.replace(/^0+/, "");
  }
  return value;
};

const SelectionMaterialRFQTable = ({
  rfqData,
  sortedSuppliers,
  selectedCheckboxes,
  setSelectedCheckboxes,
  withGst,
  closeRfqForm,
  openDropdown,
  handleMaterialDropdown,
  onTotalLowestPriceSum,
  isAdvancedTransport,
  selectedMaterialsMap,
  setSelectedMaterialsMap,
  selectedRatesTab,
  isEditRatesLocked,
  ALLOW_MULTI_VENDOR_PER_MATERIAL,
  showSelectedValueColumn = false,
  hasBudgetColumns = true,
  hiddenLineItemKeys = new Set(),
  getLineItemKey,
  renderLineItemHideButton,
}) => {
  const [lowestPrices, setLowestPrices] = useState({});
  const [brandPopupMaterial, setBrandPopupMaterial] = useState(null);
  const [openRevisionTrail, setOpenRevisionTrail] = useState(() => new Set());
  const vendorCellRefs = useRef({});
  const { enqueueSnackbar } = useSnackbar();
  const showSplitQuantityColumn = closeRfqForm || selectedRatesTab;
  // const [vendorMaterialsMap, setVendorMaterialsMap] = useState({});

  const findSelectedMaterial = (supplierId, materialId) =>
    selectedMaterialsMap?.[supplierId]?.find(
      (item) => String(item.materialId) === String(materialId),
    );

  const isMaterialSelected = (supplierId, materialId) =>
    selectedMaterialsMap?.[supplierId]?.some(
      (item) => String(item.materialId) === String(materialId),
    );

  const getSelectedQuantity = (materialEntry, vendorItem, material) =>
    parseNumber(
      materialEntry?.selectedQuantity !== undefined &&
        materialEntry?.selectedQuantity !== null
        ? materialEntry.selectedQuantity
        : parseNumber(
            vendorItem?.selectedQuantity,
            getMaterialQuantity(material),
          ),
    );

  const getSelectedQuantityForMaterial = (
    materialId,
    excludeSupplierId = null,
  ) =>
    Object.entries(selectedMaterialsMap || {}).reduce(
      (sum, [supplierId, materials]) => {
        if (
          excludeSupplierId &&
          String(supplierId) === String(excludeSupplierId)
        )
          return sum;
        const selectedMaterial = materials?.find(
          (item) => String(item.materialId) === String(materialId),
        );
        return sum + parseNumber(selectedMaterial?.selectedQuantity);
      },
      0,
    );

  const calculateSelectedTransportCost = (
    selectedTransportRate,
    selectedQuantity,
    existingTransportCost = 0,
  ) =>
    isAdvancedTransport
      ? parseNumber(selectedTransportRate) * parseNumber(selectedQuantity)
      : parseNumber(existingTransportCost);

  const calculateSelectedSupplierPrice = (
    selectedItemRate,
    selectedQuantity,
    selectedTransportCost,
  ) =>
    parseNumber(selectedItemRate) * parseNumber(selectedQuantity) +
    parseNumber(selectedTransportCost);

  const findVendorMaterialItem = (materialDetails, material, rowIndex) =>
    findQuotationLineItem(materialDetails, material, rowIndex);

  const getRevisionRemarkText = (value) =>
    String(value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();

  const formatRevisionAmount = (value) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value) || 0);

  const formatRevisionDiscount = (value) =>
    `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(value) || 0)}%`;

  const formatRevisionDifference = (difference, differencePercent) => {
    if (difference === null || difference === undefined) return "-";

    const numericDifference = Number(difference);
    if (!Number.isFinite(numericDifference)) return "-";

    return (
      <>
        {formatRevisionAmount(Math.abs(numericDifference))}{" "}
        {numericDifference < 0 && "\u25BC"}
        {numericDifference > 0 && "\u25B2"}
        {differencePercent !== null && differencePercent !== undefined && (
          <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
        )}
      </>
    );
  };

  const formatRevisionDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
  };

  const getMaterialRevisionSignature = (item) =>
    JSON.stringify({
      baseRate: parseNumber(item?.basePrice),
      discount: parseNumber(item?.discount),
      gst: parseNumber(item?.gst),
      netRate: getDiscountedItemRate(item?.basePrice, item?.discount),
      quotedRate: parseNumber(item?.quotedRate ?? item?.quotedValue ?? item?.priceWithoutGst ?? item?.netPrice),
      transportRate: parseNumber(item?.transportRate),
      transportCost: parseNumber(item?.transportCost),
      isAvailable: item?.isAvailable ?? true,
      deliveryDate: item?.deliveryDate || "",
      remark: getRevisionRemarkText(item?.remark),
    });

  const getMaterialRevisionTrail = (supplier, material, rowIndex) => {
    let previousSignature = null;

    return (supplier?.vendorQuotations?.quotationSets || [])
      .reduce((trail, quotationSet) => {
        const item = findVendorMaterialItem(
          quotationSet?.materialDetails,
          material,
          rowIndex,
        );
        if (!item) return trail;

        const signature = getMaterialRevisionSignature(item);
        if (trail.length > 0 && signature === previousSignature) return trail;

        const baseRate = parseNumber(item?.basePrice);
        const discount = parseNumber(item?.discount);
        const revisionIndex = trail.length;

        trail.push({
          label: `R${revisionIndex}`,
          date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
          submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
          baseRate,
          discount,
          netRate: getDiscountedItemRate(item?.basePrice, item?.discount),
          quotedRate: parseNumber(item?.quotedRate ?? item?.quotedValue ?? item?.priceWithoutGst ?? item?.netPrice),
          remark: getRevisionRemarkText(item?.remark),
        });
        previousSignature = signature;

        return trail;
      }, [])
      .map((revision, revisionIndex, trail) => {
        const previousRevision = trail[revisionIndex - 1];
        const netRateDifference = previousRevision
          ? revision.netRate - previousRevision.netRate
          : null;
        const netRateDifferencePercent = previousRevision && previousRevision.netRate !== 0
          ? (Math.abs(netRateDifference) / Math.abs(previousRevision.netRate)) * 100
          : null;

        return {
          ...revision,
          label: `${revision.label}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
          netRateDifference,
          netRateDifferencePercent,
        };
      });
  };

  const toggleRevisionTrail = (event, rowIndex, vendorIndex, trailLength) => {
    event.stopPropagation();
    if (trailLength <= 1) return;

    const key = `${rowIndex}-${vendorIndex}`;
    setOpenRevisionTrail((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getEntryOrCreate = (
    updatedVendorMaterials,
    selectedVendorId,
    materialId,
    vendorItem,
  ) => {
    if (!updatedVendorMaterials[selectedVendorId]) {
      updatedVendorMaterials[selectedVendorId] = [];
    }

    let materialEntry = updatedVendorMaterials[selectedVendorId].find(
      (item) => String(item.materialId) === String(materialId),
    );
    if (!materialEntry) {
      materialEntry = {
        materialId,
        selectedDiscount: vendorItem?.discount ?? 0,
        selectedTransportRate: vendorItem?.transportRate ?? 0,
        selectedTransportCost: vendorItem?.transportCost ?? 0,
        selectedItemRate: getDiscountedItemRate(
          vendorItem?.basePrice,
          vendorItem?.discount,
        ),
        selectedQuantity: getSelectedQuantity(
          null,
          vendorItem,
          rfqData?.materials?.find(
            (material) => String(material._id) === String(materialId),
          ),
        ),
      };
      materialEntry.selectedTransportCost = calculateSelectedTransportCost(
        materialEntry.selectedTransportRate,
        materialEntry.selectedQuantity,
        materialEntry.selectedTransportCost,
      );
      materialEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
        materialEntry.selectedItemRate,
        materialEntry.selectedQuantity,
        materialEntry.selectedTransportCost,
      );
      updatedVendorMaterials[selectedVendorId].push(materialEntry);
    }

    return materialEntry;
  };

  useEffect(() => {
    const newLowestPrices = {};
    const newSelectedCheckboxes = {};
    let totalLowestPriceSum = 0;
    let newVendorMaterialsMap = {};
    let selectedTransportCost = 0;

    rfqData?.materials?.forEach((material, rowIndex) => {
      let minPrice = Infinity;
      let minSupplierIds = [];

      sortedSuppliers?.forEach((supplier) => {
        const vendorQuotation =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const vendorItem = findVendorMaterialItem(
          vendorQuotation?.materialDetails,
          material,
          rowIndex,
        );

        if (vendorItem && vendorItem.basePrice > 0) {
          const discountedPrice =
            vendorItem.basePrice -
            (vendorItem.basePrice * vendorItem.discount) / 100;

          if (discountedPrice > 0) {
            if (discountedPrice < minPrice) {
              minPrice = discountedPrice;
              minSupplierIds = [supplier.supplierId];
              selectedTransportCost = vendorItem.transportCost || 0;
            } else if (discountedPrice === minPrice) {
              minSupplierIds.push(supplier.supplierId);
            }
          }
        }
      });

      newLowestPrices[material._id] = {
        minPrice: minPrice === Infinity ? 0 : minPrice,
        minSupplierIds,
      };

      // Initialize as array for multiple selections for both cases
      newSelectedCheckboxes[material._id] = [];

      if (selectedRatesTab) {
        // Initialize as array for multiple selections
        //     newSelectedCheckboxes[material._id] = [];

        //     if (minSupplierIds.length > 0) {
        //         // Select all lowest price vendors
        //         minSupplierIds.forEach(selectedVendorId => {
        //             newSelectedCheckboxes[material._id].push(selectedVendorId);

        //             if (!newVendorMaterialsMap[selectedVendorId]) {
        //                 newVendorMaterialsMap[selectedVendorId] = [];
        //             }

        //             newVendorMaterialsMap[selectedVendorId].push({
        //                 materialId: material._id,
        //                 selectedDiscount: bestVendorItem?.discount ?? material?.discount ?? 0,
        //                 selectedTransportRate: bestVendorItem?.transportRate ?? material?.transportRate ?? 0,
        //                 selectedTransportCost: bestVendorItem?.transportCost ?? material?.transportCost ?? 0,
        //                 selectedItemRate: bestVendorItem.basePrice - (bestVendorItem.basePrice * bestVendorItem?.discount) / 100,
        //                 selectedSupplierPrice: ((bestVendorItem.basePrice - (bestVendorItem.basePrice * bestVendorItem.discount) / 100) * bestVendorItem.quantity) +
        //                     (bestVendorItem?.transportCost ?? material?.transportCost ?? 0),
        //             });
        //         });
        //     }
        // } else {
        // Handle manual selections (isSelected flag)
        sortedSuppliers?.forEach((supplier) => {
          const vendorQuotation =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const vendorItem = findVendorMaterialItem(
            vendorQuotation?.materialDetails,
            material,
            rowIndex,
          );

          if (vendorItem?.isSelected) {
            const selectedVendorId = supplier.supplierId;

            // Add to selected vendors array
            newSelectedCheckboxes[material._id].push(selectedVendorId);

            if (!newVendorMaterialsMap[selectedVendorId]) {
              newVendorMaterialsMap[selectedVendorId] = [];
            }

            newVendorMaterialsMap[selectedVendorId].push({
              materialId: material._id,
              selectedDiscount: vendorItem.selectedDiscount ?? 0,
              selectedTransportRate: vendorItem.selectedTransportRate ?? 0,
              selectedQuantity: parseNumber(
                vendorItem.selectedQuantity,
                getMaterialQuantity(material),
              ),
              selectedTransportCost: calculateSelectedTransportCost(
                vendorItem.selectedTransportRate ?? 0,
                parseNumber(
                  vendorItem.selectedQuantity,
                  getMaterialQuantity(material),
                ),
                vendorItem.selectedTransportCost ?? 0,
              ),
              selectedItemRate:
                vendorItem.selectedItemRate ??
                getDiscountedItemRate(
                  vendorItem.basePrice,
                  vendorItem.selectedDiscount,
                ),
              selectedSupplierPrice:
                vendorItem.selectedSupplierPrice ??
                calculateSelectedSupplierPrice(
                  vendorItem.selectedItemRate ??
                    getDiscountedItemRate(
                      vendorItem.basePrice,
                      vendorItem.selectedDiscount,
                    ),
                  parseNumber(
                    vendorItem.selectedQuantity,
                    getMaterialQuantity(material),
                  ),
                  calculateSelectedTransportCost(
                    vendorItem.selectedTransportRate ?? 0,
                    parseNumber(
                      vendorItem.selectedQuantity,
                      getMaterialQuantity(material),
                    ),
                    vendorItem.selectedTransportCost ?? 0,
                  ),
                ),
            });
          }
        });
      }

      const transportCost = isAdvancedTransport ? selectedTransportCost : 0;
      const materialTotalCost =
        (minPrice === Infinity ? 0 : minPrice) * material.quantity +
        transportCost;
      totalLowestPriceSum += materialTotalCost;
    });

    setLowestPrices(newLowestPrices);
    setSelectedCheckboxes(newSelectedCheckboxes);
    setSelectedMaterialsMap(newVendorMaterialsMap);
    onTotalLowestPriceSum(totalLowestPriceSum);
  }, [rfqData, sortedSuppliers, selectedRatesTab]);

  // Handle manual checkbox selection
  const handleCheckboxChange = (rowIndex, selectedVendorId, vendorItem) => {
    const materialId = rfqData?.materials?.[rowIndex]?._id;
    const material = rfqData?.materials?.[rowIndex];
    const isAlreadySelected =
      selectedCheckboxes[materialId]?.includes(selectedVendorId);
    const remainingQuantity =
      getMaterialQuantity(material) -
      getSelectedQuantityForMaterial(materialId, selectedVendorId);

    if (
      !isAlreadySelected &&
      ALLOW_MULTI_VENDOR_PER_MATERIAL &&
      remainingQuantity <= 0
    ) {
      enqueueSnackbar(
        `Full RFQ quantity is already allocated for ${getPlainMaterialName(material?.name, "this material")}.`,
        {
          variant: "warning",
          autoHideDuration: 4000,
        },
      );
      return;
    }

    setSelectedCheckboxes((prev) => {
      const currentSelections = prev[materialId] || [];
      const isSelected = currentSelections.includes(selectedVendorId);

      let updatedSelections;

      if (isSelected) {
        // remove vendor
        updatedSelections = currentSelections.filter(
          (id) => id !== selectedVendorId,
        );
      } else {
        if (!ALLOW_MULTI_VENDOR_PER_MATERIAL) {
          // SINGLE MODE (temporary)
          updatedSelections = [selectedVendorId];
        } else {
          // ORIGINAL MULTI MODE
          updatedSelections = [...currentSelections, selectedVendorId];
        }
      }

      return {
        ...prev,
        [materialId]: updatedSelections,
      };
    });

    setSelectedMaterialsMap((prev) => {
      let updated = { ...prev };

      // If selecting new vendor in SINGLE mode
      if (!ALLOW_MULTI_VENDOR_PER_MATERIAL && !isAlreadySelected) {
        // remove this material from ALL other vendors
        Object.keys(updated).forEach((vendorId) => {
          updated[vendorId] = updated[vendorId]?.filter(
            (item) => String(item.materialId) !== String(materialId),
          );

          if (updated[vendorId]?.length === 0) {
            delete updated[vendorId];
          }
        });
      }

      if (isAlreadySelected) {
        // remove
        if (updated[selectedVendorId]) {
          updated[selectedVendorId] = updated[selectedVendorId].filter(
            (item) => String(item.materialId) !== String(materialId),
          );

          if (updated[selectedVendorId].length === 0) {
            delete updated[selectedVendorId];
          }
        }
      } else {
        // add
        if (!updated[selectedVendorId]) {
          updated[selectedVendorId] = [];
        }
        const selectedQuantity = remainingQuantity;
        const selectedTransportRate = vendorItem?.transportRate ?? 0;
        const selectedTransportCost = calculateSelectedTransportCost(
          selectedTransportRate,
          selectedQuantity,
          vendorItem?.transportCost ?? 0,
        );
        const selectedItemRate = getDiscountedItemRate(
          vendorItem.basePrice,
          vendorItem?.discount,
        );

        updated[selectedVendorId].push({
          materialId,
          selectedQuantity,
          selectedDiscount: vendorItem?.discount ?? 0,
          selectedTransportRate,
          selectedTransportCost,
          selectedItemRate,
          selectedSupplierPrice: calculateSelectedSupplierPrice(
            selectedItemRate,
            selectedQuantity,
            selectedTransportCost,
          ),
        });
      }

      return updated;
    });
  };

  const handleDiscountChange = (
    materialId,
    selectedVendorId,
    value,
    vendorItem,
  ) => {
    setSelectedMaterialsMap((prevVendorMaterials) => {
      const updatedVendorMaterials = { ...prevVendorMaterials };

      const materialEntry = getEntryOrCreate(
        updatedVendorMaterials,
        selectedVendorId,
        materialId,
        vendorItem,
      );
      const previousDiscount = parseNumber(materialEntry.selectedDiscount);

      // Use parseFloat safely for calculations; fallback to 0
      const discount = parseNumber(value);
      const discountBasePrice =
        previousDiscount === 0 && materialEntry.selectedItemRate !== undefined
          ? parseNumber(materialEntry.selectedItemRate)
          : parseNumber(vendorItem.basePrice);

      // Store raw string so user can type intermediate states like "10." or ".5"
      materialEntry.selectedDiscount = value;
      materialEntry.selectedItemRate = getDiscountedItemRate(
        discountBasePrice,
        discount,
      );
      const selectedQuantity = parseNumber(
        materialEntry.selectedQuantity,
        parseNumber(vendorItem?.quantity),
      );

      materialEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
        materialEntry.selectedItemRate,
        selectedQuantity,
        materialEntry?.selectedTransportCost,
      );

      return updatedVendorMaterials;
    });
  };

  const handleNetPriceChange = (
    materialId,
    selectedVendorId,
    value,
    vendorItem,
  ) => {
    setSelectedMaterialsMap((prevVendorMaterials) => {
      const updatedVendorMaterials = { ...prevVendorMaterials };
      const materialEntry = getEntryOrCreate(
        updatedVendorMaterials,
        selectedVendorId,
        materialId,
        vendorItem,
      );
      const numericValue = parseNumber(value);

      // Net price can be manually overridden independent of discount.
      // Reset discount because the existing discount belonged to the old quoted price.
      materialEntry.selectedItemRate = value;
      materialEntry.selectedDiscount = 0;
      const selectedQuantity = parseNumber(
        materialEntry.selectedQuantity,
        parseNumber(vendorItem?.quantity),
      );
      materialEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
        numericValue,
        selectedQuantity,
        materialEntry?.selectedTransportCost,
      );

      return updatedVendorMaterials;
    });
  };

  const handleTransportRateChange = (
    materialId,
    selectedVendorId,
    value,
    vendorItem,
  ) => {
    const transportRate = parseNumber(value);

    setSelectedMaterialsMap((prevVendorMaterials) => {
      let updatedVendorMaterials = { ...prevVendorMaterials };

      const materialEntry = getEntryOrCreate(
        updatedVendorMaterials,
        selectedVendorId,
        materialId,
        vendorItem,
      );

      // Update transport rate and transport cost
      materialEntry.selectedTransportRate = transportRate;
      const selectedQuantity = parseNumber(
        materialEntry.selectedQuantity,
        parseNumber(vendorItem?.quantity),
      );
      materialEntry.selectedTransportCost = calculateSelectedTransportCost(
        transportRate,
        selectedQuantity,
        materialEntry.selectedTransportCost,
      );
      const selectedItemRate = parseNumber(
        materialEntry.selectedItemRate,
        getDiscountedItemRate(
          vendorItem.basePrice,
          materialEntry.selectedDiscount,
        ),
      );
      materialEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
        selectedItemRate,
        selectedQuantity,
        materialEntry?.selectedTransportCost,
      );

      return updatedVendorMaterials;
    });
  };

  const handleSelectedQuantityChange = (
    materialId,
    selectedVendorId,
    value,
    vendorItem,
  ) => {
    const normalizedValue = normalizeRateInput(value);
    if (normalizedValue === null) return;

    setSelectedMaterialsMap((prevVendorMaterials) => {
      const updatedVendorMaterials = { ...prevVendorMaterials };
      const materialEntry = getEntryOrCreate(
        updatedVendorMaterials,
        selectedVendorId,
        materialId,
        vendorItem,
      );
      const selectedQuantity = parseNumber(normalizedValue);

      materialEntry.selectedQuantity = normalizedValue;
      materialEntry.selectedTransportCost = calculateSelectedTransportCost(
        materialEntry.selectedTransportRate,
        selectedQuantity,
        materialEntry.selectedTransportCost,
      );
      materialEntry.selectedSupplierPrice = calculateSelectedSupplierPrice(
        materialEntry.selectedItemRate,
        selectedQuantity,
        materialEntry.selectedTransportCost,
      );

      return updatedVendorMaterials;
    });
  };

  const calculatePercentageDifference = (firstPrice, latestPrice) => {
    if (!firstPrice || firstPrice === 0) return 0;
    return latestPrice - firstPrice;
  };

  const calculateDifferencePercent = (firstPrice, difference) => {
    const numericFirstPrice = Number(firstPrice);
    const numericDifference = Number(difference);

    if (!Number.isFinite(numericFirstPrice) || numericFirstPrice === 0 || !Number.isFinite(numericDifference)) {
      return null;
    }

    return (Math.abs(numericDifference) / Math.abs(numericFirstPrice)) * 100;
  };

  const getCleanText = (value) =>
    String(value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim();

  const getMaterialBrands = (material) => {
    const rawBrands = material?.brands || material?.brand || material?.approvedBrands || [];
    const brands = Array.isArray(rawBrands) ? rawBrands : String(rawBrands).split(",");

    return brands
      .map((brand) => {
        if (typeof brand === "string") return brand.trim();
        return String(brand?.name || brand?.brandName || brand?.label || brand?.value || "").trim();
      })
      .filter(Boolean);
  };

  const renderMaterialBrands = (material) => {
    const brands = getMaterialBrands(material);
    if (brands.length === 0) return null;

    const visibleBrands = brands.slice(0, 2);
    const hiddenCount = brands.length - visibleBrands.length;

    return (
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {visibleBrands.map((brand, index) => (
          <span
            key={`${brand}-${index}`}
            className="truncate text-[10px] font-medium"
            title={brand}
          >
            {brand},
          </span>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            className="text-[10px] font-semibold text-blue-700 underline hover:bg-blue-50"
            onClick={(event) => {
              event.stopPropagation();
              setBrandPopupMaterial(material);
            }}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {rfqData?.materials?.map((material, rowIndex) => {
        const lineItemKey = getLineItemKey?.("material", material, rowIndex) ?? `material:${material?._id ?? material?.id ?? rowIndex}`;
        if (hiddenLineItemKeys.has(lineItemKey)) return null;

        const hasAnyRemark = sortedSuppliers?.some((supplier) => {
          const vendorQuotation =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const vendorItem = findVendorMaterialItem(
            vendorQuotation?.materialDetails,
            material,
            rowIndex,
          );
          return Boolean(getCleanText(vendorItem?.remark));
        });

        return (
        <React.Fragment key={material._id || rowIndex}>
        <tr className="bg-white text-xs">
          <div
            className="sticky left-0 z-10 h-full bg-white flex"
            style={{ minWidth: 480, maxWidth: 480, width: 480, boxShadow: "4px 0 4px rgba(0, 0, 0, 0.1)" }}
          >
            <td className="grid grid-cols-18 gap-x-1 h-full w-full bg-white sticky py-2 left-0 px-[22px] border-b">
              {(() => {
                const isLastRateVisible = !selectedRatesTab && !closeRfqForm;
                const itemSpan = 3 + (!hasBudgetColumns ? (isLastRateVisible ? 6 : 8) : 0);

                return (
                  <>
                    {/* Serial number and row visibility */}
                    <div className="col-span-1 flex flex-col items-center gap-1">
                      <span>{rowIndex + 1}.</span>
                      {renderLineItemHideButton?.(
                        lineItemKey,
                        getPlainMaterialName(material?.name, `Material ${rowIndex + 1}`),
                      )}
                    </div>

                    {/* Item Name */}
                    <div className="w-full" style={{ gridColumn: `span ${itemSpan} / span ${itemSpan}` }}>
                      <p>
                        {ReactHtmlParser(material?.name)}
                        {(material?.code || material?.materialCode || material?.itemCode) && (
                          <span className="mt-0.5 block text-[10px] font-medium text-[#6B7280]">
                            {material?.code || material?.materialCode || material?.itemCode}
                          </span>
                        )}
                      </p>
                      {renderMaterialBrands(material)}
                    </div>

                    {/* Qty */}
                    <p className="col-span-2 min-w-0 whitespace-normal text-left" style={{ overflowWrap: "anywhere" }}>
                      {material?.quantity} {getFormattedUnit(material?.unit)}
                    </p>

                    {/* Last Rate (only when visible) */}
                    {isLastRateVisible && (
                      <p className="col-span-3 text-right">
                        {lowestPrices[material._id]?.minPrice > 0
                          ? new Intl.NumberFormat("en-IN", {
                              maximumFractionDigits: 2,
                            }).format(lowestPrices[material._id]?.minPrice)
                          : "-"}
                      </p>
                    )}

                    {hasBudgetColumns && (
                      <>
                        {/* Budget */}
                        <p
                          className={`text-right ${
                            isLastRateVisible ? "col-span-3" : "col-span-4"
                          }`}
                        >
                          {material?.budget > 0
                            ? new Intl.NumberFormat("en-IN", {
                                maximumFractionDigits: 2,
                              }).format(material?.budget)
                            : "-"}
                        </p>

                        {/* Budget Amt */}
                        <p
                          className={`text-right ${
                            isLastRateVisible ? "col-span-3" : "col-span-4"
                          }`}
                        >
                          {material?.budgetAmount > 0
                            ? new Intl.NumberFormat("en-IN", {
                                maximumFractionDigits: 2,
                              }).format(material?.budgetAmount)
                            : "-"}
                        </p>
                      </>
                    )}

                    {/* Lowest Rate (click scroll) */}
                    <p
                      className={`text-xs text-right text-blue-700 break-words cursor-pointer hover:underline ${
                        isLastRateVisible ? "col-span-3" : "col-span-4"
                      }`}
                      onClick={() => {
                        const lowestSupplierId =
                          lowestPrices[material._id]?.minSupplierIds?.[0];
                        if (!lowestSupplierId) return;

                        const vendorIndex = sortedSuppliers.findIndex(
                          (s) => s.supplierId === lowestSupplierId,
                        );
                        if (vendorIndex === -1) return;

                        const cell =
                          vendorCellRefs.current[`${rowIndex}-${vendorIndex}`];
                        if (!cell) return;

                        const scrollContainer = cell.closest(
                          ".comparison-scrollbar",
                        );
                        if (!scrollContainer) return;

                        const stickyColumn =
                          scrollContainer.querySelector(".fixed-column");
                        const stickyWidth = stickyColumn?.offsetWidth || 420;

                        const cellLeft =
                          cell.getBoundingClientRect().left -
                          scrollContainer.getBoundingClientRect().left;

                        const targetScrollLeft =
                          scrollContainer.scrollLeft + cellLeft - stickyWidth;

                        scrollContainer.scrollTo({
                          left: targetScrollLeft,
                          behavior: "smooth",
                        });
                      }}
                    >
                      {lowestPrices[material._id]?.minPrice > 0
                        ? new Intl.NumberFormat("en-IN", {
                            maximumFractionDigits: 2,
                          }).format(lowestPrices[material._id]?.minPrice)
                        : "-"}
                    </p>
                  </>
                );
              })()}
            </td>
          </div>

          {sortedSuppliers?.map((supplier, vendorIndex) => {
            const vendorQuotation =
              supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
            const firstVendorQuotation =
              supplier.vendorQuotations?.quotationSets?.[0];

            const vendorItem =
              findVendorMaterialItem(
                vendorQuotation?.materialDetails,
                material,
                rowIndex,
              ) || {};
            const firstVendorItem =
              findVendorMaterialItem(
                firstVendorQuotation?.materialDetails,
                material,
                rowIndex,
              ) || {};

            const selectedMaterial = findSelectedMaterial(
              supplier.supplierId,
              material._id,
            );
            const selectedQuantity = getSelectedQuantity(
              selectedMaterial,
              vendorItem,
              material,
            );
            const selectedQuantityForMaterial = getSelectedQuantityForMaterial(
              material._id,
            );
            const isQuantityExceeded =
              selectedQuantityForMaterial > getMaterialQuantity(material);
            const supplierPrice = selectedMaterial
              ? selectedMaterial.selectedItemRate
              : (getDiscountedItemRate(
                  vendorItem.basePrice,
                  vendorItem.discount,
                ) ?? 0);
            const firstSupplierPrice =
              getDiscountedItemRate(
                firstVendorItem.basePrice,
                firstVendorItem.discount,
              ) ?? 0;

            const isLowest = lowestPrices[
              material._id
            ]?.minSupplierIds?.includes(supplier?.supplierId);
            const isChecked = selectedCheckboxes[material._id]?.includes(
              supplier?.supplierId,
            );
            const percentageDifference = calculatePercentageDifference(
              firstSupplierPrice,
              supplierPrice,
            );
            const differencePercent = calculateDifferencePercent(
              firstSupplierPrice,
              percentageDifference,
            );
            const revisionTrail = getMaterialRevisionTrail(
              supplier,
              material,
              rowIndex,
            );
            const revisionCount = Math.max(revisionTrail.length - 1, 0);
            const isRevisionTrailOpen = openRevisionTrail.has(`${rowIndex}-${vendorIndex}`);
            const isCurrentMaterialSelected = isMaterialSelected(
              supplier.supplierId,
              material._id,
            );
            const currentDiscount = selectedMaterial
              ? selectedMaterial.selectedDiscount
              : selectedRatesTab && vendorItem?.isSelected
                ? vendorItem?.selectedDiscount
                : vendorItem?.discount;
            const currentRateSpanClass = showSelectedValueColumn
              ? "col-span-4"
              : "col-span-5";
            const splitCurrentRateSpanClass = showSplitQuantityColumn
              ? "col-span-4"
              : currentRateSpanClass;
            const firstRateSpanClass = showSplitQuantityColumn
              ? "col-span-2"
              : showSelectedValueColumn
                ? "col-span-3"
                : "col-span-4";
            const differenceSpanClass = showSplitQuantityColumn
              ? "col-span-3"
              : showSelectedValueColumn
                ? "col-span-3"
                : "col-span-4";
            const quantitySpanClass = "col-span-2";
            const transportSpanClass = "col-span-2";
            const quotedValueSpanClass = isAdvancedTransport
              ? showSplitQuantityColumn
                ? showSelectedValueColumn
                  ? "col-span-3"
                  : "col-span-5"
                : "col-span-3"
              : showSplitQuantityColumn
                ? showSelectedValueColumn
                  ? "col-span-4"
                  : "col-span-7"
                : showSelectedValueColumn
                  ? "col-span-4"
                  : "col-span-5";
            const selectedValueSpanClass = isAdvancedTransport
              ? showSplitQuantityColumn
                ? "col-span-2"
                : "col-span-3"
              : showSplitQuantityColumn
                ? "col-span-3"
                : "col-span-4";

            return (
              <td
                ref={(el) => {
                  if (el) {
                    vendorCellRefs.current[`${rowIndex}-${vendorIndex}`] = el;
                  }
                }}
                key={vendorIndex}
                className={`max-w-[540px] min-w-[540px] pt-0 px-4 z-0 align-top border border-gray-300}`}
              >
                <div
                  onClick={() => {
                    /* Legacy material-details dropdown trigger disabled for now. */
                  }}
                  className="relative py-1"
                >
                  <div className="w-full">
                    {vendorItem.deliveryDate &&
                      rfqData.providingDate &&
                      new Date(vendorItem.deliveryDate).getTime() !==
                        new Date(rfqData.providingDate).getTime() && (
                        <p className="text-xs text-[#DC7604]">
                          Proposed:{" "}
                          {new Date(vendorItem.deliveryDate).toLocaleDateString(
                            "en-GB",
                          )}
                        </p>
                      )}
                  </div>

                  <div className="grid grid-cols-18 items-start w-full gap-x-1">
                    {/* Check isAvailable: show "NA" aligned to top if not available */}
                    {"isAvailable" in vendorItem && !vendorItem.isAvailable ? (
                      <>
                        <div
                          className={`${splitCurrentRateSpanClass} flex items-start`}
                        >
                          <p className="text-xs ">NA</p>
                        </div>
                        {showSplitQuantityColumn && (
                          <div
                            className={`${quantitySpanClass} flex items-start justify-end`}
                          >
                            <p className="text-xs ">NA</p>
                          </div>
                        )}
                        <div
                          className={`${firstRateSpanClass} flex items-start justify-end`}
                        >
                          <p className="text-xs ">NA</p>
                        </div>
                        <div
                          className={`${differenceSpanClass} flex items-start justify-end`}
                        >
                          <p className="text-xs ">NA</p>
                        </div>
                        {isAdvancedTransport && (
                          <div
                            className={`${transportSpanClass} flex items-start justify-end`}
                          >
                            <p className="text-xs ">NA</p>
                          </div>
                        )}
                        <div
                          className={`${quotedValueSpanClass} flex items-start justify-end`}
                        >
                          <p className="text-xs ">NA</p>
                        </div>
                        {showSelectedValueColumn && (
                          <div
                            className={`${selectedValueSpanClass} flex items-start justify-end`}
                          >
                            <p className="text-xs ">NA</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Original content here if available */}
                        {/* Left: Price & Discount */}
                        <div
                          className={`${splitCurrentRateSpanClass} flex flex-col items-start`}
                        >
                          <div className="flex items-center">
                            {(closeRfqForm || selectedRatesTab) && (
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  handleCheckboxChange(
                                    rowIndex,
                                    supplier?.supplierId,
                                    vendorItem,
                                  )
                                }
                                className="h-3 w-3 form-checkbox text-blue-600 border-solid bg-gray-100 focus:ring-0 focus:outline-none mr-1"
                                onClick={(e) => e.stopPropagation()}
                                disabled={isEditRatesLocked}
                              />
                            )}
                            {isEditRatesLocked ? (
                              <p
                                className={`text-xs ${isLowest ? "bg-lime-200 px-1" : ""}`}
                              >
                                {isNaN(supplierPrice) || supplierPrice == null
                                  ? "0.00"
                                  : new Intl.NumberFormat("en-IN", {
                                      maximumFractionDigits: 2,
                                    }).format(supplierPrice)}
                              </p>
                            ) : (
                              <input
                                type="text"
                                title="Edit net item rate"
                                value={
                                  selectedMaterial
                                    ? String(
                                        selectedMaterial.selectedItemRate ?? "",
                                      )
                                    : supplierPrice
                                      ? String(supplierPrice)
                                      : ""
                                }
                                onChange={(e) => {
                                  const normalizedValue = normalizeRateInput(
                                    e.target.value,
                                  );
                                  if (normalizedValue === null) return;
                                  handleNetPriceChange(
                                    material._id,
                                    supplier?.supplierId,
                                    normalizedValue,
                                    vendorItem,
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || value === ".") {
                                    handleNetPriceChange(
                                      material._id,
                                      supplier?.supplierId,
                                      "0",
                                      vendorItem,
                                    );
                                  }
                                }}
                                disabled={!isCurrentMaterialSelected}
                                onClick={(e) => e.stopPropagation()}
                                className={`text-xs border border-gray-300 w-[62px] px-1 py-0 rounded focus:outline-none focus:ring-0 focus:ring-blue-200 ${isLowest ? "bg-lime-100" : ""}`}
                              />
                            )}
                          </div>

                          <div className="mt-[5px]">
                            <div className="flex flex-wrap items-center gap-1 text-xs text-[#777777]">
                              <span>Disc.:</span>

                              {isEditRatesLocked ? (
                                <span>
                                  {currentDiscount != null
                                    ? `${(Math.floor(parseNumber(currentDiscount) * 100) / 100).toFixed(2)}%`
                                    : "0.00%"}
                                </span>
                              ) : (
                                <input
                                  type="text"
                                  value={
                                    isCurrentMaterialSelected
                                      ? String(
                                          selectedMaterial?.selectedDiscount ??
                                            "",
                                        )
                                      : vendorItem?.discount != null
                                        ? String(vendorItem.discount)
                                        : ""
                                  }
                                  onChange={(e) => {
                                    const val = normalizeRateInput(
                                      e.target.value,
                                    );
                                    if (val === null) return;
                                    handleDiscountChange(
                                      material._id,
                                      supplier?.supplierId,
                                      val,
                                      vendorItem,
                                    );
                                  }}
                                  onBlur={(e) => {
                                    let val = e.target.value;
                                    if (val === "" || val === ".") {
                                      handleDiscountChange(
                                        material._id,
                                        supplier?.supplierId,
                                        "0",
                                        vendorItem,
                                      );
                                    }
                                  }}
                                  disabled={!isCurrentMaterialSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs border border-gray-300 w-10 px-1 py-0 rounded focus:outline-none focus:ring-0 focus:ring-blue-200"
                                />
                              )}
                              {vendorItem?.gst != null && vendorItem.gst !== "" && (
                                <span className="whitespace-nowrap">GST: {vendorItem.gst}%</span>
                              )}
                            </div>
                            {vendorItem?.attachments?.length > 0 && (
                              <div className="relative mt-1 ml-2">
                                <img
                                  src="https://storagereponeevaydevcdn.blob.core.windows.net/business/files.svg"
                                  alt="attachment"
                                  className="w-3 h-3"
                                />
                                <div className="bg-[#f05252] rounded-full -top-2 -right-2 h-3 w-3 flex justify-center items-center absolute">
                                  <p className="text-[10px] text-white text-center pt-[3px]">
                                    {vendorItem?.attachments?.length}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {showSplitQuantityColumn && (
                          <div
                            className={`${quantitySpanClass} flex flex-col items-end`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isCurrentMaterialSelected ? (
                              <>
                                <input
                                  type="text"
                                  title={`Selected quantity. RFQ quantity: ${material?.quantity || 0}`}
                                  value={
                                    selectedMaterial?.selectedQuantity ??
                                    selectedQuantity ??
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleSelectedQuantityChange(
                                      material._id,
                                      supplier?.supplierId,
                                      e.target.value,
                                      vendorItem,
                                    )
                                  }
                                  onBlur={(e) => {
                                    if (
                                      e.target.value === "" ||
                                      e.target.value === "."
                                    ) {
                                      handleSelectedQuantityChange(
                                        material._id,
                                        supplier?.supplierId,
                                        "0",
                                        vendorItem,
                                      );
                                    }

                                    const currentValue = parseNumber(
                                      e.target.value,
                                    );
                                    const otherSelectedQuantity =
                                      getSelectedQuantityForMaterial(
                                        material._id,
                                        supplier?.supplierId,
                                      );
                                    const rfqQuantity =
                                      getMaterialQuantity(material);
                                    if (currentValue <= 0) {
                                      enqueueSnackbar(
                                        `Please enter selected quantity for ${getPlainMaterialName(material?.name, "selected material")}.`,
                                        {
                                          variant: "warning",
                                          autoHideDuration: 4000,
                                        },
                                      );
                                    } else if (
                                      otherSelectedQuantity + currentValue >
                                      rfqQuantity
                                    ) {
                                      enqueueSnackbar(
                                        `Selected quantity for ${getPlainMaterialName(material?.name)} cannot exceed RFQ quantity ${rfqQuantity}.`,
                                        {
                                          variant: "warning",
                                          autoHideDuration: 4000,
                                        },
                                      );
                                    }
                                  }}
                                  disabled={isEditRatesLocked}
                                  className={`text-xs border w-[52px] px-1 py-0 rounded text-right focus:outline-none focus:ring-0 focus:ring-blue-200 ${isQuantityExceeded ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                />
                                <span className="text-[10px] text-gray-500 leading-3 mt-[2px]">
                                  {selectedMaterial?.selectedQuantity ??
                                    selectedQuantity ??
                                    ""}{" "}
                                  / {material?.quantity || 0}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        )}

                        {/* First Supplier Price */}
                        <p
                          className={`text-xs ${firstRateSpanClass} text-right`}
                        >
                          {firstSupplierPrice > 0
                            ? new Intl.NumberFormat("en-IN", {
                                maximumFractionDigits: 2,
                              }).format(firstSupplierPrice)
                            : "-"}
                        </p>

                        {/* % Difference */}
                        <div
                          className={`relative text-xs ${differenceSpanClass} text-right flex flex-col items-end ${
                            parseFloat(percentageDifference) < 0
                              ? "text-green-600"
                              : parseFloat(percentageDifference) > 0
                                ? "text-red-600"
                                : "text-[#777777]"
                          }`}
                        >
                          <div className="flex flex-col items-end gap-1">
                            <div className={`flex flex-col items-end ${revisionTrail.length > 1 ? "cursor-pointer rounded px-1 hover:bg-gray-50" : ""}`}>
                              <span>
                                {Math.abs(parseFloat(percentageDifference)).toFixed(2)}{" "}
                                {parseFloat(percentageDifference) < 0 && "\u25BC"}
                                {parseFloat(percentageDifference) > 0 && "\u25B2"}
                              </span>
                              <span className="text-[10px] leading-3">
                                {differencePercent === null ? "-" : "(" + differencePercent.toFixed(2) + "%)"}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Transport */}
                        {isAdvancedTransport && (
                          <div
                            className={`${transportSpanClass} flex flex-col items-end`}
                          >
                            <p className="text-xs text-[#777777]">
                              {new Intl.NumberFormat("en-IN", {
                                maximumFractionDigits: 2,
                              }).format(
                                selectedRatesTab && vendorItem?.isSelected
                                  ? (vendorItem?.selectedTransportRate ?? 0)
                                  : (vendorItem?.transportRate ?? 0),
                              )}
                            </p>
                            {!isEditRatesLocked && (
                              <input
                                type="text"
                                value={
                                  isCurrentMaterialSelected
                                    ? selectedMaterial?.selectedTransportRate
                                    : vendorItem?.transportRate
                                }
                                onChange={(e) =>
                                  handleTransportRateChange(
                                    material._id,
                                    supplier?.supplierId,
                                    e.target.value,
                                    vendorItem,
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                                disabled={!isCurrentMaterialSelected}
                                className="text-xs border border-gray-300 w-[50px] mt-1 px-1 py-0 rounded focus:outline-none focus:ring-0 focus:ring-blue-200"
                              />
                            )}
                          </div>
                        )}

                        {/* Total */}
                        <div
                          className={`flex flex-col items-end justify-start ${quotedValueSpanClass}`}
                        >
                          <p className="text-xs font-normal text-[#2D2D11]">
                            {isCurrentMaterialSelected
                              ? new Intl.NumberFormat("en-IN", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(
                                  selectedMaterial?.selectedSupplierPrice ?? 0,
                                )
                              : new Intl.NumberFormat("en-IN", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(vendorItem?.priceWithoutGst ?? 0)}
                          </p>
                          {revisionTrail.length > 1 && (
                            <button
                              type="button"
                              className="mt-[2px] inline-flex items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                              onClick={(event) => toggleRevisionTrail(event, rowIndex, vendorIndex, revisionTrail.length)}
                            >
                              {isRevisionTrailOpen
                                ? "Hide revisions"
                                : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
                            </button>
                          )}
                        </div>

                        {showSelectedValueColumn && (
                          <div
                            className={`flex items-center justify-end ${selectedValueSpanClass}`}
                          >
                            <p className="text-xs font-medium text-[#2D2D11]">
                              {isCurrentMaterialSelected
                                ? new Intl.NumberFormat("en-IN", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  }).format(
                                    selectedMaterial?.selectedSupplierPrice ??
                                      0,
                                  )
                                : "-"}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {isRevisionTrailOpen && (
                    <div
                      className="mt-2 w-[calc(100%_+_24px)] min-w-[520px] rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="grid grid-cols-[38px_108px_56px_42px_58px_64px_minmax(95px,1fr)] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                        <span>Round</span>
                        <span className="text-center">Date</span>
                        <span className="text-right">Rate</span>
                        <span className="text-right">Disc.</span>
                        <span className="text-right">Net rate</span>
                        <span className="text-right">Difference</span>
                        <span className="text-right">Quoted Amount</span>
                      </div>
                      {revisionTrail.map((revision, revisionIndex) => (
                        <React.Fragment key={`${revision.label}-${revisionIndex}`}>
                          <div
                            className={`grid grid-cols-[38px_108px_56px_42px_58px_64px_minmax(95px,1fr)] gap-x-2 border-b ${revision.remark ? "border-gray-200" : "border-gray-300"} py-2`}
                          >
                            <span className={`font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>{revision.label}</span>
                            <span className="whitespace-normal break-words text-center leading-4">
                              <span className="block">{revision.submittedBy}</span>
                              <span className="block">{revision.date}</span>
                            </span>
                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.baseRate)}</span>
                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionDiscount(revision.discount)}</span>
                            <span className={`text-right font-semibold ${revisionIndex === revisionTrail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                              {formatRevisionAmount(revision.netRate)}
                            </span>
                            <span className={`text-right font-semibold ${revision.netRateDifference < 0 ? "text-emerald-600" : revision.netRateDifference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                              {formatRevisionDifference(revision.netRateDifference, revision.netRateDifferencePercent)}
                            </span>
                            <span className="text-right font-medium text-[#2D2D11]">{formatRevisionAmount(revision.quotedRate)}</span>
                          </div>
                          {revision.remark && (
                            <div className="border-b border-gray-300 py-1 text-gray-600">
                              <span className="font-semibold">Remarks:</span>{" "}{revision.remark}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
        {hasAnyRemark && (
          <tr className="bg-white text-xs">
            <div
              className="sticky left-0 z-10 h-full bg-white flex"
              style={{ minWidth: 480, maxWidth: 480, width: 480, boxShadow: "4px 0 4px rgba(0, 0, 0, 0.1)" }}
            >
              <td className="sticky left-0 h-full w-full border-b bg-white px-[22px] py-2">
                <p className="text-[11px] font-semibold italic text-[#6B7280]">Vendor Remarks</p>
              </td>
            </div>
            {sortedSuppliers?.map((supplier, vendorIndex) => {
              const vendorQuotation =
                supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
              const vendorItem = findVendorMaterialItem(
                vendorQuotation?.materialDetails,
                material,
                rowIndex,
              );
              const remark = getCleanText(vendorItem?.remark);

              return (
                <td
                  key={`remark-${material._id || rowIndex}-${vendorIndex}`}
                  className="max-w-[540px] min-w-[540px] border border-gray-300 px-4 py-2"
                >
                  {remark ? (
                    <p className="whitespace-normal break-words text-[11px] italic leading-4 text-[#555555]">
                      {ReactHtmlParser(vendorItem?.remark)}
                    </p>
                  ) : (
                    <span className="text-[11px] text-gray-300">-</span>
                  )}
                </td>
              );
            })}
          </tr>
        )}
        </React.Fragment>
      );
      })}
      {brandPopupMaterial && (
        <StaticPopupNew
          openModal={Boolean(brandPopupMaterial)}
          handleClose={() => setBrandPopupMaterial(null)}
          header="Approved Brands"
          width="md"
          bodyStyle="p-4"
        >
          <div className="flex flex-wrap gap-2">
            {getMaterialBrands(brandPopupMaterial).map((brand, index) => (
              <span
                key={`${brand}-${index}`}
                className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
              >
                {brand}
              </span>
            ))}
          </div>
        </StaticPopupNew>
      )}
    </>
  );
};

export default SelectionMaterialRFQTable;
